import { useEffect, useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch } from '../../components/ui.tsx';
import { t, tn } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { day, moneyIn, parseRate, rateText, todayIso } from '../../lib/format.ts';
import { useSession } from '../../lib/session.tsx';
import { ManageLayout, useAction, useSave } from './common.tsx';

export interface CurrencyRow {
  id: string;
  code: string;
  name: string;
  symbol: string | null;
  active: boolean;
  /** Where its rates come from: people, or the European Central Bank every working day. */
  feed: 'manual' | 'ecb';
  latest: { rate: number; date: string } | null;
  previous: number | null;
}
export interface CurrencyList {
  base: string;
  currencies: CurrencyRow[];
}
interface RateRow {
  id: string;
  currency: string;
  date: string;
  rate: number;
  source: 'manual' | 'import' | 'ecb';
}
/** What the European Central Bank can give this business. */
interface EcbInfo {
  base: string;
  available: boolean;
  currencies: string[];
  latest: string | null;
}

/** Codes offered when adding a currency; any other three-letter code can be typed. */
const COMMON = ['EUR', 'USD', 'GBP', 'CHF', 'MKD', 'RSD', 'TRY', 'CNY', 'JPY', 'CAD', 'AUD', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'RON', 'BGN', 'AED', 'ALL'];
const CODE = /^[A-Z]{3}$/;

/** Currency names in the person's language, from the browser ("Euro", "Dollari amerikan"). */
export function currencyName(code: string, fallback?: string) {
  try {
    return new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'currency' }).of(code) ?? fallback ?? code;
  } catch {
    return fallback ?? code;
  }
}

/** The currencies in use and their rates, for the pickers on invoices and bills. */
export function useCurrencies() {
  return useAction<CurrencyList>('company.currencies');
}

/**
 * Other currencies the business invoices and is billed in. The books stay in the business's own
 * currency; each rate is entered here (typed or pasted from the bank's list), never fetched.
 */
export function Currencies() {
  const { can } = useSession();
  const { data, reload } = useCurrencies();
  const { data: ecb } = useAction<EcbInfo>('company.ecb');
  const { busy: fetching, run } = useSave();
  const [adding, setAdding] = useState(false);
  const [pasting, setPasting] = useState(false);
  const [rateFor, setRateFor] = useState<CurrencyRow | null>(null);
  const [history, setHistory] = useState<CurrencyRow | null>(null);
  const edit = can('company.rates');
  const active = data?.currencies.filter((c) => c.active) ?? [];
  const off = data?.currencies.filter((c) => !c.active) ?? [];
  const stale = active.filter((c) => !c.latest || c.latest.date < todayIso());
  // Currencies in use the ECB publishes, when it publishes the books' own currency too.
  const fromEcb = ecb?.available ? active.filter((c) => ecb.currencies.includes(c.code)) : [];
  const getEcb = () =>
    void run(async () => {
      const r = await action<{ saved: number; skipped: string[] }>('company.fetch_rates', { codes: fromEcb.map((c) => c.code) });
      await reload();
      return r;
    }, t('fx.ecb_done'));

  return (
    <ManageLayout
      title={t('fx.title')}
      subtitle={data ? t('fx.subtitle', { base: data.base }) : undefined}
      actions={
        edit ? (
          <>
            {fromEcb.length ? (
              <Button icon="download" onClick={getEcb} busy={fetching}>
                {t('fx.ecb_get')}
              </Button>
            ) : null}
            <Button icon="copy" onClick={() => setPasting(true)} disabled={!data}>
              {t('fx.paste')}
            </Button>
            <Button variant="primary" icon="plus" onClick={() => setAdding(true)} disabled={!data}>
              {t('fx.add')}
            </Button>
          </>
        ) : null
      }
    >
      {!data ? (
        <Skeleton h={200} r="var(--radius-lg)" />
      ) : !data.currencies.length ? (
        <EmptyState
          icon="globe"
          title={t('fx.empty', { base: data.base })}
          body={t('fx.empty_body', { base: data.base })}
          action={
            edit ? (
              <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
                {t('fx.add')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          {stale.length ? (
            <div className="callout callout-info">
              <Icon name="clock" size={18} />
              <span>{t('fx.stale', { codes: stale.map((c) => c.code).join(', ') })}</span>
            </div>
          ) : null}
          <div className="list-card fx-list" role="list" aria-label={t('fx.title')}>
            {active.map((c) => (
              <CurrencyLine key={c.id} c={c} base={data.base} edit={edit} onRate={() => setRateFor(c)} onOpen={() => setHistory(c)} />
            ))}
          </div>
          {off.length ? (
            <>
              <h2 className="section-title">{t('fx.not_in_use')}</h2>
              <div className="list-card fx-list" role="list">
                {off.map((c) => (
                  <CurrencyLine key={c.id} c={c} base={data.base} edit={edit} onRate={() => setRateFor(c)} onOpen={() => setHistory(c)} />
                ))}
              </div>
            </>
          ) : null}
          <p className="muted fx-note">
            <Icon name="info" size={14} /> {t('fx.how', { base: data.base })}
          </p>
          {ecb ? (
            <p className="muted fx-note">
              <Icon name="bank" size={14} /> {ecb.available ? t('fx.ecb_how') : t('fx.ecb_not_base', { base: ecb.base })}
            </p>
          ) : null}
        </>
      )}
      {adding && data ? (
        <AddDialog
          list={data}
          ecb={ecb}
          onClose={() => setAdding(false)}
          onDone={async () => {
            setAdding(false);
            await reload();
          }}
        />
      ) : null}
      {pasting && data ? (
        <PasteDialog
          base={data.base}
          onClose={() => setPasting(false)}
          onDone={async () => {
            await reload();
          }}
        />
      ) : null}
      {rateFor && data ? (
        <RateDialog
          c={rateFor}
          base={data.base}
          onClose={() => setRateFor(null)}
          onDone={async () => {
            setRateFor(null);
            await reload();
          }}
        />
      ) : null}
      {history && data ? (
        <HistoryDialog
          c={history}
          base={data.base}
          edit={edit}
          ecb={!!ecb?.available && ecb.currencies.includes(history.code)}
          onClose={() => setHistory(null)}
          onChange={reload}
          onRate={() => (setHistory(null), setRateFor(history))}
        />
      ) : null}
    </ManageLayout>
  );
}

function CurrencyLine({ c, base, edit, onRate, onOpen }: { c: CurrencyRow; base: string; edit: boolean; onRate: () => void; onOpen: () => void }) {
  const change = c.latest && c.previous ? (c.latest.rate - c.previous) / c.previous : 0;
  return (
    <div role="listitem" className={`list-row${c.active ? '' : ' archived'}`}>
      <span className="fx-code" aria-hidden="true">
        {c.code}
      </span>
      <button className="list-main" onClick={onOpen}>
        <span className="list-title">{currencyName(c.code, c.name)}</span>
        <span className="list-sub">
          {c.latest ? (
            <>
              <span className="mono">{t('fx.one_is', { code: c.code, rate: rateText(c.latest.rate), base })}</span>
              <span>· {day(c.latest.date)}</span>
            </>
          ) : (
            <span className="text-warn">{t('fx.no_rate')}</span>
          )}
        </span>
      </button>
      {change ? (
        <span className={`fx-change ${change > 0 ? 'up' : 'down'}`} title={t('fx.since_previous')}>
          <Icon name={change > 0 ? 'arrowUp' : 'arrowDown'} size={14} />
          {Math.abs(change * 100).toFixed(2)} %
        </span>
      ) : null}
      {c.feed === 'ecb' && c.active ? <StatusBadge tone="info" icon="bank" label={t('fx.ecb_badge')} /> : null}
      {!c.active ? <StatusBadge tone="neutral" icon="pause" label={t('fx.off')} /> : null}
      {edit ? (
        <Button size="sm" icon="plus" onClick={onRate}>
          {t('fx.new_rate')}
        </Button>
      ) : null}
    </div>
  );
}

function AddDialog({ list, ecb, onClose, onDone }: { list: CurrencyList; ecb: EcbInfo | null; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const taken = new Set([list.base, ...list.currencies.map((c) => c.code)]);
  const first = COMMON.find((c) => !taken.has(c)) ?? '';
  const [code, setCode] = useState(first);
  const [other, setOther] = useState(false);
  const [rate, setRate] = useState('');
  const [date, setDate] = useState(todayIso());
  const [tried, setTried] = useState(false);
  const upper = code.trim().toUpperCase();
  const n = parseRate(rate);
  // The ECB publishes both this currency and the books' own: its rates can come from there.
  const ecbCan = !!ecb?.available && ecb.currencies.includes(upper);
  const [follow, setFollow] = useState(true);
  const fromEcb = ecbCan && follow;
  const errors = {
    code: !CODE.test(upper) ? t('fx.code_rule') : upper === list.base ? t('fx.is_base', { base: list.base }) : taken.has(upper) ? t('fx.already') : null,
    rate: rate.trim() && n === null ? t('fx.rate_rule') : null,
  };
  const save = () => {
    setTried(true);
    if (errors.code || (!fromEcb && errors.rate)) return;
    void run(async () => {
      await action('company.save_currency', { code: upper, name: currencyName(upper), rate: fromEcb ? undefined : (n ?? undefined), date: !fromEcb && n ? date : undefined });
      if (fromEcb) await action('company.rate_feed', { code: upper, feed: 'ecb' });
      await onDone();
    }, t('fx.added', { code: upper }));
  };
  return (
    <Dialog
      title={t('fx.add')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy}>
            {t('fx.add_short')}
          </Button>
        </>
      }
    >
      <form
        className="form-stack"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        {other ? (
          <Field label={t('fx.code')} hint={t('fx.code_hint')} error={tried || code ? errors.code : null}>
            <input className="input mono" value={code} maxLength={3} autoCapitalize="characters" onChange={(e) => setCode(e.target.value.toUpperCase())} data-autofocus />
          </Field>
        ) : (
          <Field label={t('fx.currency')} error={tried ? errors.code : null}>
            <select className="input" value={code} onChange={(e) => (e.target.value === '*' ? (setOther(true), setCode('')) : setCode(e.target.value))} data-autofocus>
              {COMMON.filter((c) => !taken.has(c)).map((c) => (
                <option key={c} value={c}>
                  {c} · {currencyName(c)}
                </option>
              ))}
              <option value="*">{t('fx.other_code')}</option>
            </select>
          </Field>
        )}
        {ecbCan ? (
          <label className="check-row">
            <input type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)} />
            <span>
              <span>{t('fx.ecb_follow')}</span>
              <span className="muted">{t('fx.ecb_follow_hint')}</span>
            </span>
          </label>
        ) : null}
        {fromEcb ? null : (
          <div className="form-grid">
            <Field label={t('fx.rate_label', { code: upper || '…', base: list.base })} hint={t('fx.first_rate_hint')} error={errors.rate}>
              <input className="input mono" inputMode="decimal" value={rate} placeholder="97.85" onChange={(e) => setRate(e.target.value)} />
            </Field>
            <Field label={t('fx.date')}>
              <input className="input" type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} />
            </Field>
          </div>
        )}
      </form>
    </Dialog>
  );
}

function RateDialog({ c, base, onClose, onDone }: { c: CurrencyRow; base: string; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [rate, setRate] = useState(c.latest ? rateText(c.latest.rate).replace(/[^\d.,]/g, '') : '');
  const [date, setDate] = useState(todayIso());
  const n = parseRate(rate);
  const save = () =>
    n &&
    void run(async () => {
      await action('company.save_rate', { currency: c.code, date, rate: n });
      await onDone();
    }, t('fx.rate_saved', { code: c.code }));
  return (
    <Dialog
      title={t('fx.new_rate_for', { code: c.code })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={!n || !date}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form
        className="form-stack"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <Field label={t('fx.rate_label', { code: c.code, base })} error={rate.trim() && !n ? t('fx.rate_rule') : null} hint={c.latest ? t('fx.last_was', { rate: rateText(c.latest.rate), date: day(c.latest.date) }) : undefined}>
          <input className="input mono" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('fx.date')} hint={t('fx.date_hint')}>
          <input className="input" type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} />
        </Field>
        {n ? <p className="muted">{t('fx.example', { amount: moneyIn(100_00, c.code), base: moneyIn(Math.round(100_00 * n), base) })}</p> : null}
      </form>
    </Dialog>
  );
}

function PasteDialog({ base, onClose, onDone }: { base: string; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [text, setText] = useState('');
  const [date, setDate] = useState(todayIso());
  const [result, setResult] = useState<{ saved: number; bad: { line: number; text: string }[]; skipped: string[] } | null>(null);
  const go = () =>
    void run(async () => {
      const r = await action<{ saved: number; bad: { line: number; text: string }[]; skipped: string[] }>('company.import_rates', { text, date });
      setResult(r);
      await onDone();
      if (!r.bad.length && !r.skipped.length) onClose();
    });
  return (
    <Dialog
      title={t('fx.paste')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {result ? t('common.close') : t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={go} busy={busy} disabled={!text.trim()}>
            {t('fx.paste_save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('fx.paste_hint', { base })}</p>
        <Field label={t('fx.paste_label')}>
          <textarea className="input textarea mono" rows={7} value={text} placeholder={'EUR 97.85\nUSD 90.10\n2026-10-27, GBP, 112.40'} onChange={(e) => (setText(e.target.value), setResult(null))} data-autofocus />
        </Field>
        <Field label={t('fx.paste_date')} hint={t('fx.paste_date_hint')}>
          <input className="input" type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} />
        </Field>
        {result ? (
          <div className={`callout ${result.bad.length || result.skipped.length ? 'callout-warn' : 'callout-ok'}`} role="status">
            <Icon name={result.bad.length ? 'alert' : 'checkCircle'} size={18} />
            <div>
              <p className="strong">{tn('fx.paste_saved', result.saved)}</p>
              {result.bad.map((b) => (
                <p key={b.line} className="muted">
                  {t('fx.paste_bad', { line: b.line, text: b.text })}
                </p>
              ))}
              {result.skipped.length ? <p className="muted">{t('fx.paste_skipped', { list: result.skipped.join(', ') })}</p> : null}
            </div>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

function HistoryDialog({
  c,
  base,
  edit,
  ecb,
  onClose,
  onChange,
  onRate,
}: {
  c: CurrencyRow;
  base: string;
  edit: boolean;
  /** The ECB publishes this currency and the books' own. */
  ecb: boolean;
  onClose: () => void;
  onChange: () => Promise<void>;
  onRate: () => void;
}) {
  const [feed, setFeed] = useState(c.feed);
  const { busy, run } = useSave();
  const { data, reload } = useAction<RateRow[]>('company.rates', { currency: c.code, limit: 120 });
  const [removing, setRemoving] = useState<RateRow | null>(null);
  const [toggling, setToggling] = useState(false);
  const rates = data ?? [];
  const max = Math.max(...rates.map((r) => r.rate), 1);
  const min = Math.min(...rates.map((r) => r.rate), max);
  const toggle = () =>
    void run(async () => {
      await action('company.save_currency', { code: c.code, name: c.name, active: !c.active });
      await onChange();
      onClose();
    }, c.active ? t('fx.turned_off', { code: c.code }) : t('fx.turned_on', { code: c.code })).finally(() => setToggling(false));
  return (
    <Dialog
      title={`${c.code} · ${currencyName(c.code, c.name)}`}
      onClose={onClose}
      size="lg"
      footer={
        <>
          {edit ? (
            <Button variant="ghost" icon={c.active ? 'pause' : 'undo'} className="dialog-left" onClick={() => (c.active ? setToggling(true) : toggle())} disabled={busy}>
              {c.active ? t('fx.turn_off') : t('fx.turn_on')}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
          {edit ? (
            <Button variant="primary" icon="plus" onClick={onRate}>
              {t('fx.new_rate')}
            </Button>
          ) : null}
        </>
      }
    >
      {!data ? (
        <Skeleton h={200} />
      ) : (
        <div className="form-stack">
          {ecb ? (
            <div className="fx-feed">
              <span>
                <strong>{t('fx.ecb_follow')}</strong>
                <span className="muted small">{t('fx.ecb_follow_hint')}</span>
              </span>
              <Switch
                checked={feed === 'ecb'}
                label={t('fx.ecb_follow')}
                disabled={!edit || busy || !c.active}
                onChange={(on) =>
                  void run(async () => {
                    await action('company.rate_feed', { code: c.code, feed: on ? 'ecb' : 'manual' });
                    setFeed(on ? 'ecb' : 'manual');
                    await reload();
                    await onChange();
                  }, on ? t('fx.ecb_on', { code: c.code }) : t('fx.ecb_off', { code: c.code }))
                }
              />
            </div>
          ) : null}
          {!rates.length ? <EmptyState icon="globe" title={t('fx.no_rate')} /> : null}
          {rates.length ? (
            <>
              {rates.length > 1 ? (
                <div className="fx-spark" role="img" aria-label={t('fx.trend', { from: rateText(rates[rates.length - 1]!.rate), to: rateText(rates[0]!.rate) })}>
                  {[...rates].reverse().map((r) => (
                    <span key={r.id} style={{ height: `${max === min ? 60 : 15 + ((r.rate - min) / (max - min)) * 85}%` }} title={`${day(r.date)} · ${rateText(r.rate)}`} />
                  ))}
                </div>
              ) : null}
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('fx.date')}</th>
                    <th className="num">{t('fx.one_unit', { code: c.code, base })}</th>
                    <th>{t('fx.source')}</th>
                    {edit ? <th aria-label={t('common.delete')} /> : null}
                  </tr>
                </thead>
                <tbody>
                  {rates.map((r) => (
                    <tr key={r.id}>
                      <td>{day(r.date)}</td>
                      <td className="num mono">{rateText(r.rate)}</td>
                      <td className="muted">{r.source === 'ecb' ? t('fx.src_ecb') : r.source === 'import' ? t('fx.src_import') : t('fx.src_manual')}</td>
                      {edit ? (
                        <td className="num">
                          <IconButton icon="trash" label={t('fx.delete_rate', { date: day(r.date) })} onClick={() => setRemoving(r)} />
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="muted">{t('fx.history_hint')}</p>
            </>
          ) : null}
        </div>
      )}
      {removing ? (
        <Confirm
          title={t('fx.delete_rate', { date: day(removing.date) })}
          body={t('fx.delete_rate_body')}
          confirm={t('common.delete')}
          danger
          busy={busy}
          onClose={() => setRemoving(null)}
          onConfirm={() =>
            void run(async () => {
              await action('company.delete_rate', { id: removing.id });
              setRemoving(null);
              await reload();
              await onChange();
            }, t('fx.rate_deleted'))
          }
        />
      ) : null}
      {toggling ? (
        <Confirm
          title={t('fx.turn_off')}
          body={t('fx.turn_off_body', { code: c.code })}
          confirm={t('fx.turn_off')}
          busy={busy}
          onClose={() => setToggling(false)}
          onConfirm={toggle}
        />
      ) : null}
    </Dialog>
  );
}

/** The rate a document dated that day would use; undefined while it loads, null when there is none. */
export function useDayRate(code: string, date: string) {
  const [r, setR] = useState<{ rate: number; date: string } | null | undefined>(undefined);
  useEffect(() => {
    setR(undefined);
    if (!code || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    let live = true;
    action<{ rate: number; date: string } | null>('company.rate_on', { currency: code, date }).then(
      (v) => live && setR(v),
      () => live && setR(null),
    );
    return () => {
      live = false;
    };
  }, [code, date]);
  return r;
}

/** The rate a document will use, in millionths: the one typed, else the day's; null when there is none. */
export function effectiveRate(typed: string, day: { rate: number } | null | undefined): number | null {
  if (typed.trim()) {
    const n = parseRate(typed);
    return n ? Math.round(n * 1_000_000) : null;
  }
  return day?.rate ?? null;
}

/** Converts an amount in the other currency at a rate in millionths, as the server does. */
export const toBase = (minor: number, rate: number) => Math.sign(minor) * Math.round((Math.abs(minor) * rate) / 1_000_000);

/**
 * The currency of a document and its rate: the business's own by default; another one in use
 * shows its rate for the document's date, which can be typed over (the bank's rate on the bill).
 */
export function CurrencyFields({
  list,
  currency,
  onCurrency,
  rate,
  onRate,
  date,
  dayRate,
}: {
  list: CurrencyList;
  currency: string;
  onCurrency: (code: string) => void;
  rate: string;
  onRate: (v: string) => void;
  date: string;
  dayRate: { rate: number; date: string } | null | undefined;
}) {
  const codes = list.currencies.filter((c) => c.active || c.code === currency).map((c) => c.code);
  const bad = !!rate.trim() && parseRate(rate) === null;
  return (
    <>
      <Field label={t('fx.currency')} hint={currency ? undefined : t('fx.in_base', { base: list.base })}>
        <select className="input" value={currency} onChange={(e) => onCurrency(e.target.value)}>
          <option value="">
            {list.base} · {currencyName(list.base)}
          </option>
          {codes.map((c) => (
            <option key={c} value={c}>
              {c} · {currencyName(c)}
            </option>
          ))}
        </select>
      </Field>
      {currency ? (
        <Field
          label={t('fx.rate_label', { code: currency, base: list.base })}
          error={bad ? t('fx.rate_rule') : !rate.trim() && dayRate === null ? t('fx.no_rate_day', { code: currency, date: day(date) }) : null}
          hint={
            dayRate && !rate.trim()
              ? t('fx.day_rate_hint', { date: day(dayRate.date) })
              : dayRate && rate.trim()
                ? t('fx.typed_rate_hint', { rate: rateText(dayRate.rate) })
                : undefined
          }
        >
          <input className="input mono" inputMode="decimal" value={rate} placeholder={dayRate ? String(dayRate.rate / 1_000_000) : ''} onChange={(e) => onRate(e.target.value)} />
        </Field>
      ) : null}
    </>
  );
}
